import { TransformsList } from 'cio/utils/analytics/transforms';
import { generateFilteredTimeFrameData } from 'cio/utils/analytics/transforms/metrics';
import { isEmpty } from '@ember/utils';
import actionInfo from 'cio/utils/action-info';
import { ActionType } from 'cio/models/action';
import { Periods, MEASURE_LABELS as DELIVERY_MEASURE_LABELS } from 'cio/utils/metrics/common';
import { MEASURE_LABELS as JOURNEY_MEASURE_LABELS } from 'cio/utils/journey-metrics';

const DATA_TYPE_LABELS: Record<string, string> = {
  campaign: 'Campaigns',
  newsletter: 'Newsletters',
  broadcast: 'Broadcasts',
  transactional: 'Transactional',
};

const EDIT_TYPE_LABELS: Record<string, string> = {
  dataType: 'Data Type Changed',
  tags: 'Tags Changed',
  name: 'Filtered By Name Changed',
  excludeArchived: 'Exclude Archived Changed',
  timeRange: 'Timeframe Changed',
  tab: 'Selected Tab Changed',
  tableMetrics: 'Table Metrics Changed',
  tableChannels: 'Table Channels Changed',
  sortBy: 'Table Sort By Changed',
  sortDirection: 'Table Sort Order Changed',
  chartType: 'Chart Type Changed',
  chartUnit: 'Chart Unit Changed',
  chartGrouping: 'Chart Grouping Changed',
  chartMetric: 'Chart Metric Changed',
  chartChannels: 'Chart Channels Changed',
  selectedLines: 'Chart Selected Lines Count Changed',
};

const TAB_LABELS: Record<string, string> = {
  table: 'Table',
  chart: 'Chart',
};

const MEASURE_LABELS: Record<string, string> = {
  ...Object.keys(DELIVERY_MEASURE_LABELS).reduce(
    (acc, key) => {
      acc[`delivery_metrics.${key}`] = (DELIVERY_MEASURE_LABELS as any)[key];
      return acc;
    },
    {} as Record<string, string>,
  ),
  ...Object.keys(JOURNEY_MEASURE_LABELS).reduce(
    (acc, key) => {
      acc[`journey_metrics.${key}`] = (JOURNEY_MEASURE_LABELS as any)[key];
      return acc;
    },
    {} as Record<string, string>,
  ),
  'journey_metrics.completed': 'Completed',
};

const SORT_ORDER_LABELS: Record<string, string> = {
  asc: 'Ascending',
  desc: 'Descending',
};

const CHART_TYPE_LABELS: Record<string, string> = {
  individual: 'Individual',
  aggregate: 'Aggregate',
};

const CHART_UNIT_LABELS: Record<string, string> = {
  count: 'Number',
  percentage: 'Percentage',
};

const CHART_GROUPING_LABELS: Record<string, string> = {
  [Periods.DAYS]: 'Daily',
  [Periods.WEEKS]: 'Weekly',
  [Periods.MONTHS]: 'Monthly',
};

const Transforms: TransformsList = {
  reportCriteriaChanged(data) {
    return {
      'Report ID': data.reportId,
      'Data Type': data.dataTypes.map((type: string) => DATA_TYPE_LABELS[type]),
      'Tag Count': data.tagIds.length,
      'Filtered By Name': !isEmpty(data.name),
      'Exclude Archived': data.excludeArchived,
      ...generateFilteredTimeFrameData(data),
      'Edit Type': EDIT_TYPE_LABELS[data.editType],
    };
  },

  reportStarted(data) {
    return {
      'Report ID': data.reportId,
      'Data Type': data.dataTypes.map((type: string) => DATA_TYPE_LABELS[type]),
      'Tag Count': data.tagIds.length,
      'Filtered By Name': !isEmpty(data.name),
      'Exclude Archived': data.excludeArchived,
      ...generateFilteredTimeFrameData(data),
    };
  },

  reportCompleted(data) {
    return {
      'Report ID': data.reportId,
      'Data Type': data.dataTypes.map((type: string) => DATA_TYPE_LABELS[type]),
      'Tag Count': data.tagIds.length,
      'Filtered By Name': !isEmpty(data.name),
      'Exclude Archived': data.excludeArchived,
      ...generateFilteredTimeFrameData(data),
      'Query Time': data.queryTime,
      'Row Count': data.rowCount,
    };
  },

  refineResultsStarted(data) {
    return {
      'Report ID': data.reportId,
      'Total Rows': data.totalRows,
      'Selected Rows Count': data.selectedRows,
      'Selected Tab': TAB_LABELS[data.tab],
    };
  },

  refineResultsCompleted(data) {
    return {
      'Report ID': data.reportId,
      'Total Rows': data.totalRows,
      'Selected Rows Count': data.selectedRows,
      'Change in Selected Rows': data.selectedRows - data.previousSelectedRows,
    };
  },

  displayOptionChanged(data) {
    return {
      'Report ID': data.reportId,
      'Edit Type': EDIT_TYPE_LABELS[data.editType],
      'Selected Tab': TAB_LABELS[data.tab],
      Metrics: data.metrics.map((metric: string) => MEASURE_LABELS[metric]),
      Channels: data.channels.map((channel: string) => actionInfo(`${channel}_action` as ActionType).analyticsName),
      'Table Sorted By': data.sortBy === 'campaign_name' ? 'Name' : MEASURE_LABELS[data.sortBy],
      'Table Sort Order': SORT_ORDER_LABELS[data.sortDirection],
      'Chart Type': CHART_TYPE_LABELS[data.chartType],
      'Chart Unit': CHART_UNIT_LABELS[data.chartUnit],
      'Chart Grouping': CHART_GROUPING_LABELS[data.chartGrouping],
      'Chart Metric': MEASURE_LABELS[data.chartMetric],
      'Chart Selected Lines Count': data.selectedLinesCount,
    };
  },

  chartHover(data) {
    return {
      'Report ID': data.reportId,
      'Chart Type': CHART_TYPE_LABELS[data.chartType],
      'Chart Unit': CHART_UNIT_LABELS[data.chartUnit],
      'Chart Grouping': CHART_GROUPING_LABELS[data.chartGrouping],
      'Chart Metric': MEASURE_LABELS[data.chartMetric],
      'Chart Selected Lines Count': data.selectedLinesCount,
      'Time of Hover': data.time,
    };
  },
};

export default Transforms;
